import { Button, Flex, List, Modal, Select, Text } from '@/components';
import type { CascaderOption } from '@/components/Form/CascaderField';
import {
  useOrganizationTemplate,
  useOrganizationTemplatesByJoinOrganization,
} from '@/features/organization-template/_hooks/organization-template';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  onSelectTemplate: (organizationPaths: string[][]) => void;
  organizationOptions: CascaderOption[] | undefined;
  disabled?: boolean;
};

/**
 * 組織オプションからIDをキーとしたMapを構築するヘルパー関数
 *
 * @param options - CascaderOption配列
 * @param currentPath - 現在のパス（再帰用）
 * @returns 組織ID → {path: 階層パス, name: 組織名} のMap
 */
const buildOrganizationMap = (
  options: CascaderOption[],
  currentPath: string[] = [],
): Map<string, { path: string[]; name: string }> => {
  const map = new Map<string, { path: string[]; name: string }>();

  for (const option of options) {
    const newPath = [...currentPath, option.value as string];
    map.set(option.value as string, {
      path: newPath,
      name: option.label as string,
    });

    if (option.children) {
      const childMap = buildOrganizationMap(option.children, newPath);
      for (const [key, value] of childMap) {
        map.set(key, value);
      }
    }
  }

  return map;
};

/**
 * テンプレート選択モーダルコンポーネント
 *
 * 組織テンプレートを選択し、テンプレートの組織を関連組織に追加する
 */
export const OrganizationTemplateSelectButton: FC<Props> = ({
  onSelectTemplate,
  organizationOptions,
  disabled,
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );

  const templates = useOrganizationTemplatesByJoinOrganization((data) =>
    data.map((template) => ({
      value: template.id,
      label: template.name,
    })),
  );

  const templateDetail = useOrganizationTemplate(selectedTemplateId ?? '');

  // 組織オプションからMapを構築（パフォーマンス最適化）
  const organizationMap = useMemo(() => {
    if (!organizationOptions) {
      return new Map();
    }
    return buildOrganizationMap(organizationOptions);
  }, [organizationOptions]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTemplateId(null);
  };

  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
  };

  const handleApplyTemplate = () => {
    if (!templateDetail.data || organizationMap.size === 0) {
      return;
    }

    const organizationPaths: string[][] = [];

    for (const orgId of templateDetail.data.organizationIds) {
      const orgData = organizationMap.get(orgId);
      if (orgData) {
        organizationPaths.push(orgData.path);
      }
    }

    onSelectTemplate(organizationPaths);
    handleCloseModal();
  };

  const organizationNames = useMemo(
    () =>
      templateDetail.data && organizationMap.size > 0
        ? templateDetail.data.organizationIds
            .map((orgId) => organizationMap.get(orgId)?.name)
            .filter((name): name is string => name !== undefined)
        : [],
    [templateDetail.data, organizationMap],
  );

  return (
    <>
      <Button
        style={{ width: '100%' }}
        size="small"
        type="default"
        onClick={handleOpenModal}
        disabled={disabled}
      >
        {t('organization-template.select-from-template')}
      </Button>
      <Modal
        title={t('organization-template.select-template')}
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={[
          <Button key="cancel" onClick={handleCloseModal}>
            {t('cancel')}
          </Button>,
          <Button
            key="apply"
            type="primary"
            onClick={handleApplyTemplate}
            disabled={!selectedTemplateId || organizationNames.length === 0}
            loading={!!selectedTemplateId && templateDetail.isPending}
          >
            {t('apply')}
          </Button>,
        ]}
        width={600}
      >
        <Flex vertical gap={16}>
          <Select
            style={{ width: '100%' }}
            placeholder={t('organization-template.select-template')}
            options={templates.data}
            value={selectedTemplateId}
            onChange={handleSelectTemplate}
            loading={templates.isPending}
            allowClear
          />
          {selectedTemplateId && organizationNames.length > 0 && (
            <Flex vertical gap={8}>
              <Text
                weight="bold"
                variant="body"
                text={t('organization-template.template-organizations')}
              />
              <List
                bordered
                dataSource={organizationNames}
                renderItem={(name) => (
                  <List.Item style={{ padding: '4px 8px' }}>{name}</List.Item>
                )}
                style={{ maxHeight: 300, overflow: 'auto' }}
              />
            </Flex>
          )}
        </Flex>
      </Modal>
    </>
  );
};
